/**
 * ⚠️ DEPRECATED PATTERN - UPDATE NEEDED ⚠️
 * 
 * This hook is using the deprecated DataContext adapter pattern.
 * It should be updated to use the direct Supabase client instead:
 * 
 * import { supabase } from '@/utils/supabase/client';
 * 
 * See @/utils/supabase/examples.tsx for usage patterns.
 */

import { useState, useEffect } from "react";
import { OrgRole } from "@/utils/constants/roles";
import { Location } from "../../../utils/shared-types";
import { createLogger } from '@/utils/logger/devLogger';
import { formatDateOnly } from '@/utils/formatters/dateUtils';
import { isValidEmailAddress } from '@/utils/validation/emailValidation';

export interface LocationAssignment {
  location_id: string;
  role: string;
  is_primary: boolean;
}

export interface UserFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: OrgRole | "";
  avatar_url: string;
  status: string;
  // Personal information
  date_of_birth?: string;
  gender?: string;
  // Address fields
  street_address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  // Emergency contact
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  // Multi-location support
  primary_location_id?: string;
  has_company_access?: boolean;
  staff_privileges: boolean;
  locations?: LocationAssignment[];
  // Password fields for direct account creation
  password?: string;
  confirmPassword?: string;
}

export interface InviteOptions {
  setupMethod: 'email' | 'direct'; // Radio button choice
  sendSMS: boolean; // Keep for backwards compatibility if needed
}

export interface UseUserFormProps {
  initialData?: Partial<UserFormData>;
  onSubmit: (userData: UserFormData, inviteOptions: InviteOptions) => Promise<void>;
}

const formLogger = createLogger('UserForm');

export const useUserForm = ({ initialData, onSubmit }: UseUserFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: any}>({});
  const [formData, setFormData] = useState<UserFormData>({
    first_name: initialData?.first_name || "",
    last_name: initialData?.last_name || "",
    email: initialData?.email || "",
    phone: initialData?.phone || "",
    role: initialData?.role || "",
    avatar_url: initialData?.avatar_url || "",
    status: initialData?.status || "active",
    // Personal information
    date_of_birth: initialData?.date_of_birth,
    gender: initialData?.gender || "",
    // Address fields
    street_address: initialData?.street_address,
    city: initialData?.city,
    state: initialData?.state,
    zip_code: initialData?.zip_code,
    // Emergency contact
    emergency_contact_name: initialData?.emergency_contact_name,
    emergency_contact_phone: initialData?.emergency_contact_phone,
    emergency_contact_relationship: initialData?.emergency_contact_relationship,
    // Multi-location support
    primary_location_id: initialData?.primary_location_id || "",
    has_company_access: initialData?.has_company_access || false,
    staff_privileges: initialData?.staff_privileges || false,
    locations: initialData?.locations || [],
    // Password fields for direct account creation
    password: initialData?.password || "",
    confirmPassword: initialData?.confirmPassword || ""
  });
  const [inviteOptions, setInviteOptions] = useState<InviteOptions>({
    setupMethod: 'email', // Default to email invitation (recommended)
    sendSMS: false
  });
  const [touched, setTouched] = useState<{[key: string]: boolean}>({});

  // Format phone number: (XXX) XXX-XXXX
  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Format based on length
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  // Validate email format
  const validateEmail = (email: string): boolean => {
    return isValidEmailAddress(email);
  };

  // Validate phone format
  const validatePhone = (phone: string): boolean => {
    // Check if it has at least 10 digits
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 10;
  };

  // Format and validate date of birth to ensure 4-digit year
  const formatDateOfBirth = (value: string): string => {
    if (!value) return value;
    
    // Parse the date
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    
    // Check if the year is valid (4 digits and within reasonable range)
    const year = date.getFullYear();
    if (year < 1900 || year > new Date().getFullYear()) {
      return value; // Return original if invalid, let validation handle the error
    }
    
    // Return formatted date as YYYY-MM-DD
    return formatDateOnly(date);
  };

  // Detect Safari for dynamic error messages
  const isSafari = () => {
    if (typeof window === 'undefined') return false;
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  };

  // Auto-format MM/DD/YYYY for Safari (copied from DateOfBirthField)
  const formatDateInput = (input: string): string => {
    // Remove all non-digits
    const digitsOnly = input.replace(/\D/g, '');
    
    // Don't format if empty
    if (!digitsOnly) return '';
    
    // Limit to 8 digits max (mmddyyyy)
    const limitedDigits = digitsOnly.slice(0, 8);
    
    // Auto-format based on length
    if (limitedDigits.length <= 2) {
      // Just month: "07"
      return limitedDigits;
    } else if (limitedDigits.length <= 4) {
      // Month + day: "07/26"
      return `${limitedDigits.slice(0, 2)}/${limitedDigits.slice(2)}`;
    } else {
      // Month + day + year: "07/26/2000"
      const month = limitedDigits.slice(0, 2);
      const day = limitedDigits.slice(2, 4);
      const year = limitedDigits.slice(4);
      return `${month}/${day}/${year}`;
    }
  };

  const validateDateOfBirth = (dateString: string): boolean => {
    console.log('🔍 VALIDATING DATE OF BIRTH:', { dateString, type: typeof dateString });
    
    if (!dateString) return true; // Optional field
    
    let date: Date;
    let workingDateString = dateString;
    
    // Auto-format Safari input if it's unformatted digits
    if (isSafari() && /^\d{8}$/.test(dateString)) {
      // Convert 01011998 → 01/01/1998 for Safari
      workingDateString = formatDateInput(dateString);
      console.log('🔧 Auto-formatting unformatted Safari input:', dateString, '→', workingDateString);
    }
    
    // Handle different date formats
    if (workingDateString.includes('/')) {
      // MM/DD/YYYY format (Safari)
      console.log('📅 Parsing MM/DD/YYYY format');
      const parts = workingDateString.split('/');
      console.log('📅 Date parts:', parts);
      
      if (parts.length === 3) {
        const month = parseInt(parts[0], 10) - 1; // Month is 0-indexed
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        console.log('📅 Parsed values:', { month: month + 1, day, year }); // Show month as 1-indexed for clarity
        date = new Date(year, month, day);
        console.log('📅 Created Date object:', date);
      } else {
        console.log('❌ Invalid MM/DD/YYYY format - wrong number of parts');
        return false; // Invalid format
      }
    } else {
      // YYYY-MM-DD format (other browsers)
      console.log('📅 Parsing YYYY-MM-DD format');
      date = new Date(workingDateString);
      console.log('📅 Created Date object:', date);
    }
    
    if (isNaN(date.getTime())) {
      console.log('❌ Invalid date - NaN');
      return false;
    }
    
    const year = date.getFullYear();
    const currentYear = new Date().getFullYear();
    
    console.log('📅 Year check:', { year, currentYear, valid: year >= 1900 && year <= currentYear });
    
    // Ensure 4-digit year within reasonable range
    if (year < 1900 || year > currentYear) {
      console.log('❌ Year out of range');
      return false;
    }
    
    // Ensure the date is not in the future
    const now = new Date();
    const isInFuture = date > now;
    console.log('📅 Future check:', { date, now, isInFuture });
    
    if (isInFuture) {
      console.log('❌ Date is in the future');
      return false;
    }
    
    console.log('✅ Date validation passed');
    return true;
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    
    // Debug logging for avatar_url changes
    if (name === 'avatar_url') {
      formLogger.info('Avatar URL changed in form:', {
        hasValue: !!value,
        length: value?.length || 0,
        isBase64: value?.startsWith('data:image/') || false,
        preview: value ? value.substring(0, 50) + '...' : 'empty'
      });
    }
    
    // Special handling for phone field
    if (name === 'phone') {
      // Allow user to type, but limit what gets stored to maintain format
      processedValue = formatPhoneNumber(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Mark field as touched
    if (!touched[name]) {
      setTouched(prev => ({
        ...prev,
        [name]: true
      }));
    }
  };

  // Handle input blur
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    
    // Format phone number on blur
    if (name === 'phone' && value) {
      const formattedPhone = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        phone: formattedPhone
      }));
    }
    
    // Format and validate date of birth on blur
    if (name === 'date_of_birth' && value) {
      const formattedDate = formatDateOfBirth(value);
      setFormData(prev => ({
        ...prev,
        date_of_birth: formattedDate
      }));
    }
    
    // Live validation on blur
    validateField(name, value);
  };

  // Validate password strength
  const validatePassword = (password: string): boolean => {
    if (!password) return true; // Optional for now - will be required in form validation
    return password.length >= 8;
  };

  // Validate a specific field
  const validateField = (name: string, value: string): boolean => {
    let error = '';
    let isValid = true;

    switch (name) {
      case 'email':
        if (value && !validateEmail(value)) {
          error = 'Please enter a valid email address';
          isValid = false;
        }
        break;
      
      case 'phone':
        if (value && !validatePhone(value)) {
          error = 'Please enter a valid 10-digit phone number';
          isValid = false;
        }
        break;
        
      case 'first_name':
        if (!value.trim()) {
          error = 'First name is required';
          isValid = false;
        }
        break;
        
      case 'last_name':
        if (!value.trim()) {
          error = 'Last name is required';
          isValid = false;
        }
        break;
        
      case 'date_of_birth':
        if (value && !validateDateOfBirth(value)) {
          const format = isSafari() ? 'MM/DD/YYYY' : 'YYYY-MM-DD';
          error = `Please enter a valid date with a 4-digit year (${format}). Date cannot be in the future.`;
          isValid = false;
        }
        break;
        
      case 'password':
        if (value && !validatePassword(value)) {
          error = 'Password must be at least 8 characters long';
          isValid = false;
        }
        break;
        
      case 'confirmPassword':
        if (value && value !== formData.password) {
          error = 'Passwords do not match';
          isValid = false;
        }
        break;
    }
    
    // Update error state
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
    
    return isValid;
  };

  // Live validation effect
  useEffect(() => {
    // Validate email when changed and touched
    if (touched.email && formData.email) {
      validateField('email', formData.email);
    }
    
    // Validate phone when changed and touched
    if (touched.phone && formData.phone) {
      validateField('phone', formData.phone);
    }
    
    // Validate date of birth when changed and touched
    if (touched.date_of_birth && formData.date_of_birth) {
      validateField('date_of_birth', formData.date_of_birth);
    }
    
    // Validate password when changed and touched
    if (touched.password && formData.password) {
      validateField('password', formData.password);
    }
    
    // Validate confirm password when changed and touched
    if (touched.confirmPassword && formData.confirmPassword) {
      validateField('confirmPassword', formData.confirmPassword);
    }
  }, [formData.email, formData.phone, formData.date_of_birth, formData.password, formData.confirmPassword, touched.email, touched.phone, touched.date_of_birth, touched.password, touched.confirmPassword]);

  const handleRoleChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      role: value as OrgRole
    }));
    
    // Clear error when field is edited
    if (errors.role) {
      setErrors(prev => ({
        ...prev,
        role: ""
      }));
    }
  };

  const handleStatusChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      status: value
    }));
  };

  const handleGenderChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      gender: value
    }));
  };

  // Add new handler for checkbox change
  const handleCheckboxChange = (name: string, checked: boolean) => {
    if (name === "has_company_access") {
      // Special handling for company access toggle
      if (checked) {
        // When enabling company-wide access:
        // 1. Save the current locations list for potential restoration
        const currentLocations = [...(formData.locations || [])];
        const primaryLocationId = formData.primary_location_id || 
          (currentLocations.find(loc => loc.is_primary)?.location_id || "");
        
        // 2. Update form data: enable company access and clear location assignments
        setFormData(prev => ({
          ...prev,
          [name]: checked,
          locations: [], // Clear location assignments when company-wide access is enabled
          primary_location_id: primaryLocationId // Preserve the primary location if it exists
        }));
        
        // 3. Clear location errors if any
        setErrors(prev => {
          const { locations, ...rest } = prev;
          return rest;
        });
      } else {
        // When disabling company-wide access:
        // 1. Restore at least the primary location if set
        const restoredLocations: LocationAssignment[] = [];
        
        if (formData.primary_location_id) {
          restoredLocations.push({
          location_id: formData.primary_location_id,
          role: formData.role,
          is_primary: true,
        });
      }
        
        // 2. Update form data
        setFormData(prev => ({
          ...prev,
          [name]: checked,
          locations: restoredLocations
        }));
        
        // 3. Show validation error if no locations are assigned
        if (restoredLocations.length === 0) {
          setErrors(prev => ({
            ...prev,
            locations: "At least one location assignment is required"
          }));
        }
      }
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
    
    // Special handling for SMS (keep for backwards compatibility)
    if (name === "sendSMS") {
      setInviteOptions(prev => ({
        ...prev,
        [name]: checked
      }));
      
      // If SMS is checked, ensure phone is required
      if (checked && !formData.phone) {
        setErrors(prev => ({
          ...prev,
          phone: "Phone number is required for SMS invitation"
        }));
        
        // Mark phone as touched if SMS is enabled
        setTouched(prev => ({
          ...prev,
          phone: true
        }));
      } else if (!checked) {
        // If SMS is unchecked, remove the phone error
        setErrors(prev => {
          const newErrors = {...prev};
          delete newErrors.phone;
          return newErrors;
        });
      }
    }
  };

  // Handle setup method change (radio button)
  const handleSetupMethodChange = (method: 'email' | 'direct') => {
    setInviteOptions(prev => ({
      ...prev,
      setupMethod: method
    }));
    
    // Clear any related errors
    setErrors(prev => {
      const newErrors = {...prev};
      delete newErrors.invite;
      return newErrors;
    });
  };

  // Handle primary location change
  const handlePrimaryLocationChange = (locationId: string) => {
    setFormData(prev => ({
      ...prev,
      primary_location_id: locationId
    }));
    
    // Clear error if present
    if (errors.primary_location_id) {
      setErrors(prev => ({
        ...prev,
        primary_location_id: ""
      }));
    }
    
    // Don't auto-add the location to assignments
    // This allows maintaining the empty state
  };

  // Add a location assignment (toggle-checklist: always receives a concrete location ID)
  const handleAddLocation = (locationId: string) => {
    if (!locationId || typeof locationId !== 'string') return;

    setFormData(prev => {
      const newLocations = [...(prev.locations || [])];

      if (newLocations.some(loc => loc.location_id === locationId)) {
        return prev;
      }

      newLocations.push({
        location_id: locationId,
        role: "",
        is_primary: newLocations.length === 0,
      });

      const autoSetPrimary = newLocations.length === 1 && !prev.primary_location_id;

      return {
        ...prev,
        locations: newLocations,
        ...(autoSetPrimary ? { primary_location_id: locationId } : {})
      };
    });

    if (errors.locations) {
      setErrors(prev => ({ ...prev, locations: "" }));
    }
  };

  // Remove a location assignment by index; auto-promotes next remaining location as primary
  const handleRemoveLocation = (index: number) => {
    setFormData(prev => {
      const newLocations = [...(prev.locations || [])];
      const removingLocation = newLocations[index];

      newLocations.splice(index, 1);

      const wasPrimary = removingLocation?.location_id === prev.primary_location_id;
      const newPrimary = wasPrimary
        ? (newLocations[0]?.location_id || "")
        : prev.primary_location_id;

      return {
        ...prev,
        locations: newLocations,
        primary_location_id: newPrimary,
      };
    });
  };
  
  // Update a specific location assignment field
  const handleLocationChange = (index: number, field: string, value: string | boolean) => {
    setFormData(prev => {
      if (!prev.locations) return prev;
      
      const newLocations = [...prev.locations];
      
      // Update the field
      newLocations[index] = {
        ...newLocations[index],
        [field]: value
      };
      
      // Special handling for location_id
      if (field === "location_id") {
        // Clear errors for this location
        setErrors(prevErrors => {
          const newErrors = {...prevErrors};
          if (newErrors.locations && Array.isArray(newErrors.locations)) {
            newErrors.locations[index] = {};
          }
          return newErrors;
        });
        
        // If this is the first/only location and no primary_location_id is set,
        // automatically set this as the primary location
        if (newLocations.length === 1 && value && !prev.primary_location_id) {
          return {
            ...prev,
            locations: newLocations,
            primary_location_id: value as string
          };
        }
      }
      
      // Special handling for is_primary
      if (field === "is_primary" && value === true) {
        // Unset is_primary for all other locations
        newLocations.forEach((loc, i) => {
          if (i !== index) {
            loc.is_primary = false;
          }
        });
        
        // Update primary_location_id to match this location
        return {
          ...prev,
          locations: newLocations,
          primary_location_id: newLocations[index].location_id
        };
      }
      
      return {
        ...prev,
        locations: newLocations
      };
    });
  };

  const validateLocations = (): boolean => {
    let isValid = true;
    const locationErrors: any[] = [];
    
    // Skip validation if company-wide access is granted
    if (formData.has_company_access) {
      // Clear any existing location errors if company-wide access is granted
      setErrors(prev => {
        const { locations, ...rest } = prev;
        return rest;
      });
      return true;
    }
    
    if (!formData.locations || formData.locations.length === 0) {
      setErrors(prev => ({
        ...prev,
        locations: "At least one location assignment is required"
      }));
      return false;
    }
    
    // Auto-populate missing roles from main form role
    const updatedLocations = formData.locations.map((loc, index) => {
      if (!loc.role && formData.role) {
        return { ...loc, role: formData.role };
      }
      return loc;
    });
    
    // Update form data if any roles were populated
    const hasUpdates = updatedLocations.some((loc, index) => loc.role !== formData.locations![index].role);
    if (hasUpdates) {
      setFormData(prev => ({
        ...prev,
        locations: updatedLocations
      }));
      // Use updated locations for validation
      formData.locations = updatedLocations;
    }
    
    // Check for duplicate locations
    const locationIds = new Set<string>();
    const duplicateIds = new Set<string>();
    
    formData.locations.forEach((loc, index) => {
      if (loc.location_id && locationIds.has(loc.location_id)) {
        duplicateIds.add(loc.location_id);
      } else if (loc.location_id) {
        locationIds.add(loc.location_id);
      }
    });
    
    if (duplicateIds.size > 0) {
      setErrors(prev => ({
        ...prev,
        locations: "Duplicate locations are not allowed"
      }));
      return false;
    }
    
    // Validate each location assignment
    formData.locations.forEach((loc, index) => {
      const locationError: any = {};
      
      if (!loc.location_id) {
        locationError.location_id = "Location is required";
        isValid = false;
      }
      
      if (!loc.role) {
        locationError.role = "Role is required";
        isValid = false;
      }
      
      // Add the errors for this location
      locationErrors[index] = locationError;
    });
    
    return isValid;
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};
    let isValid = true;
    
    // Clear all previous errors so stale errors don't persist
    setErrors({});
    
    // Required fields validation
    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
      isValid = false;
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
      isValid = false;
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Invalid email format";
      isValid = false;
    }
    
    if (inviteOptions.sendSMS && !formData.phone.trim()) {
      newErrors.phone = "Phone number is required for SMS invitation";
      isValid = false;
    } else if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = "Please enter a valid 10-digit phone number";
      isValid = false;
    }
    
    // Role is always required now since role selector is always visible
    if (!formData.role) {
      newErrors.role = "Please select a role";
      isValid = false;
    }

    // Only validate location assignments if staff_privileges is enabled
    if (formData.staff_privileges) {
      // Company-wide access validation
      if (formData.has_company_access) {
        // If company-wide access is enabled, need at least one primary location 
        // selected in the location assignments
        const hasPrimaryLocation = 
          formData.primary_location_id || 
          formData.locations?.some(loc => loc.is_primary);
        
        if (!hasPrimaryLocation) {
          newErrors.locations = "Please select a primary location for this user";
          isValid = false;
        }
      } else {
        // Only validate location assignments if company-wide access is disabled
        const locationsValid = validateLocations();
        isValid = isValid && locationsValid;
      }
    }
    
    // Make sure an invitation method is selected (setupMethod is required)
    if (inviteOptions && !inviteOptions.setupMethod) {
      newErrors.invite = "Please select an account setup method";
      isValid = false;
    }
    
    // Password validation for direct account creation
    if (inviteOptions.setupMethod === 'direct') {
      // Password is optional for direct creation - if provided, validate it
      if (formData.password) {
        if (!validatePassword(formData.password)) {
          newErrors.password = "Password must be at least 8 characters long";
          isValid = false;
        }
        
        // If password is provided, confirm password is required
        if (!formData.confirmPassword) {
          newErrors.confirmPassword = "Please confirm your password";
          isValid = false;
        } else if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = "Passwords do not match";
          isValid = false;
        }
      }
      // If confirmPassword is provided without password, that's an error
      else if (formData.confirmPassword) {
        newErrors.password = "Please enter a password";
        isValid = false;
      }
    }
    
    // If validation failed, add a form-level error that tells the user exactly what's wrong
    if (!isValid) {
      const issues: string[] = [];
      if (newErrors.first_name) issues.push('First Name');
      if (newErrors.last_name) issues.push('Last Name');
      if (newErrors.email) issues.push('Email');
      if (newErrors.phone) issues.push('Phone');
      if (newErrors.role) issues.push('Role');
      if (newErrors.locations) issues.push('Location Assignment');
      if (newErrors.password) issues.push('Password');
      if (newErrors.confirmPassword) issues.push('Confirm Password');
      if (newErrors.invite) issues.push('Account Setup Method');
      
      newErrors.form = issues.length > 0
        ? `Please fix the following: ${issues.join(', ')}.`
        : 'Please complete all required fields before submitting.';
    }
    
    setErrors(prev => ({
      ...prev,
      ...newErrors
    }));
    
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Debug logging for form submission
    formLogger.info('Form submission started:', {
      hasAvatarUrl: !!formData.avatar_url,
      avatarUrlLength: formData.avatar_url?.length || 0,
      isBase64: formData.avatar_url?.startsWith('data:image/') || false,
      formDataKeys: Object.keys(formData),
      avatarPreview: formData.avatar_url ? formData.avatar_url.substring(0, 50) + '...' : 'empty'
    });
    
    // Mark all fields as touched for validation
    const allFields = Object.keys(formData);
    const allTouched = allFields.reduce((acc, field) => ({
      ...acc,
      [field]: true
    }), {});
    setTouched(allTouched);
    
    // Validate the form
    const validationResult = validateForm();
    
    if (!validationResult) {
      formLogger.warn('Form submission blocked - validation failed');
      // Scroll to the first field with an error so the user sees the feedback
      setTimeout(() => {
        const firstError = document.querySelector('[class*="text-error"]');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }
    
    // Clear form-level error from previous failed validation attempt
    setErrors(prev => {
      const { form, ...rest } = prev;
      return rest;
    });
    
    setIsLoading(true);
    
    try {
      await onSubmit(formData, inviteOptions);
      formLogger.info('Form submission completed successfully');
    } catch (error) {
      formLogger.error("Error saving user:", error);
      setErrors(prev => ({
        ...prev,
        form: "Failed to save user. Please try again."
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    formData,
    inviteOptions,
    isLoading,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    handleRoleChange,
    handleStatusChange,
    handleGenderChange,
    handleCheckboxChange,
    handleSetupMethodChange,
    handlePrimaryLocationChange,
    handleAddLocation,
    handleRemoveLocation,
    handleLocationChange,
    handleSubmit,
    setErrors
  };
}; 